import { Eye, EyeOff, LoaderCircle, Search, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import {
  Button as AriaButton,
  FieldError,
  Group,
  Input,
  Label,
  SearchField as AriaSearchField,
  Text,
  TextField as AriaTextField,
  type ButtonProps as AriaButtonProps,
} from 'react-aria-components';
import { cx } from '../cx.ts';
import { normalizeDigits } from '../format.ts';

/* ───────── Button ───────── */

/**
 * `positive` (approve/unlock) and `caution` (administrative override) are the admin action semantics
 * (owner review §31); `danger` is reserved for reject / revoke / reset / lock.
 */
export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'positive' | 'caution';
export type ButtonSize = 'sm' | 'md' | 'lg';

const VARIANT: Record<ButtonVariant, string> = {
  primary:
    'bg-primary text-on-primary font-bold shadow-cta hover:brightness-110 data-[pressed]:brightness-95',
  secondary:
    'bg-surface-2 text-fg border border-line-strong hover:bg-surface-3 data-[pressed]:bg-surface-3',
  ghost: 'bg-transparent text-fg hover:bg-surface-2 data-[pressed]:bg-surface-3',
  danger:
    'bg-negative-tint text-negative border border-negative/40 hover:bg-negative/15 data-[pressed]:bg-negative/20',
  positive:
    'bg-positive-tint text-positive border border-positive/40 hover:bg-positive/15 data-[pressed]:bg-positive/20',
  caution:
    'bg-accent-tint text-accent-text border border-accent-soft/60 hover:bg-accent/15 data-[pressed]:bg-accent/20',
};
const SIZE: Record<ButtonSize, string> = {
  sm: 'h-9 px-3.5 text-[13px] rounded-sm gap-1.5',
  md: 'h-11 px-5 text-sm rounded-md gap-2',
  lg: 'h-13 px-6 text-[15px] rounded-md gap-2.5',
};

export interface ButtonProps extends Omit<AriaButtonProps, 'children' | 'className'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner, keeps width stable and blocks presses (prevents double submit). */
  loading?: boolean;
  fullWidth?: boolean;
  icon?: ReactNode;
  className?: string;
  children: ReactNode;
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading,
  fullWidth,
  icon,
  className,
  children,
  isDisabled,
  ...rest
}: ButtonProps) {
  return (
    <AriaButton
      {...rest}
      isDisabled={isDisabled}
      isPending={loading}
      className={cx(
        'relative inline-flex items-center justify-center whitespace-nowrap select-none',
        'transition-[transform,background-color,filter,box-shadow] duration-[var(--hx-duration-fast)] ease-standard',
        'data-[pressed]:scale-[0.97] outline-hidden data-[focus-visible]:outline-2 data-[focus-visible]:outline-solid data-[focus-visible]:outline-focus data-[focus-visible]:outline-offset-2',
        'data-[disabled]:opacity-45 data-[disabled]:cursor-not-allowed cursor-pointer',
        VARIANT[variant],
        SIZE[size],
        fullWidth && 'w-full',
        className,
      )}
    >
      {({ isPending }) => (
        <>
          {/* opacity, not visibility: the label must remain the button's accessible name while pending */}
          <span className={cx('inline-flex items-center gap-[inherit]', isPending && 'opacity-0')}>
            {icon}
            {children}
          </span>
          {isPending && (
            <LoaderCircle aria-hidden className="absolute size-5 animate-spin" strokeWidth={2} />
          )}
        </>
      )}
    </AriaButton>
  );
}

export interface IconButtonProps extends Omit<AriaButtonProps, 'children' | 'className'> {
  /** Required accessible name (Persian). */
  label: string;
  children: ReactNode;
  variant?: 'surface' | 'ghost';
  className?: string;
  badge?: boolean;
}

/** 40px visual inside a 44px hit area (DESIGN_SYSTEM §4). */
export function IconButton({
  label,
  children,
  variant = 'surface',
  className,
  badge,
  ...rest
}: IconButtonProps) {
  return (
    <AriaButton
      {...rest}
      aria-label={label}
      className={cx(
        'relative inline-grid size-11 place-items-center rounded-md text-fg cursor-pointer outline-hidden',
        'transition-[background-color,transform] duration-[var(--hx-duration-fast)] data-[pressed]:scale-95',
        'data-[focus-visible]:outline-2 data-[focus-visible]:outline-solid data-[focus-visible]:outline-focus data-[focus-visible]:outline-offset-2',
        variant === 'surface'
          ? 'bg-surface-1 border border-line hover:bg-surface-2'
          : 'hover:bg-surface-2',
        className,
      )}
    >
      {children}
      {badge && (
        <span
          aria-hidden
          className="absolute top-2.5 end-2.5 size-2 rounded-full bg-negative ring-2 ring-surface-1"
        />
      )}
    </AriaButton>
  );
}

/* ───────── Fields ───────── */

const FIELD_SHELL =
  'flex items-center gap-2 rounded-md bg-surface-2 border border-line px-3.5 h-12 transition-colors ' +
  'data-[focus-within]:border-accent-soft data-[invalid]:border-negative data-[disabled]:opacity-50';
// ≥ 16px on touch screens: iOS Safari zooms (and the page jumps) when a smaller field gets focus.
const INPUT =
  'min-w-0 flex-1 bg-transparent outline-hidden text-fg placeholder:text-fg-subtle text-[15px] pointer-coarse:text-[16px]';

interface FieldBaseProps {
  label: string;
  description?: string;
  errorMessage?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  isInvalid?: boolean;
  className?: string;
  name?: string;
  autoComplete?: string;
}

export interface TextFieldProps extends FieldBaseProps {
  value?: string;
  defaultValue?: string;
  onChange?: (v: string) => void;
  placeholder?: string;
  type?: 'text' | 'tel' | 'email';
  /** Technical / numeric inputs render LTR (phone, codes, IDs). */
  ltr?: boolean;
  inputMode?: 'text' | 'tel' | 'numeric' | 'decimal' | 'email';
}

function FieldFrame({
  label,
  description,
  errorMessage,
  children,
}: Pick<FieldBaseProps, 'label' | 'description' | 'errorMessage'> & { children: ReactNode }) {
  return (
    <>
      <Label className="mb-1.5 block text-[13px] font-medium text-fg-muted">{label}</Label>
      {children}
      {description && (
        <Text slot="description" className="mt-1.5 block text-xs text-fg-subtle">
          {description}
        </Text>
      )}
      <FieldError className="mt-1.5 block text-xs text-negative">{errorMessage}</FieldError>
    </>
  );
}

export function TextField({
  label,
  description,
  errorMessage,
  className,
  ltr,
  placeholder,
  inputMode,
  type = 'text',
  ...rest
}: TextFieldProps) {
  return (
    <AriaTextField {...rest} type={type} className={cx('flex flex-col', className)}>
      <FieldFrame label={label} description={description} errorMessage={errorMessage}>
        <Group className={FIELD_SHELL}>
          <Input
            placeholder={placeholder}
            inputMode={inputMode}
            className={cx(INPUT, ltr && 'hx-ltr font-mono text-start')}
          />
        </Group>
      </FieldFrame>
    </AriaTextField>
  );
}

export function PasswordField({
  label,
  description,
  errorMessage,
  className,
  ...rest
}: Omit<TextFieldProps, 'type' | 'ltr'>) {
  const [visible, setVisible] = useState(false);
  return (
    <AriaTextField
      {...rest}
      type={visible ? 'text' : 'password'}
      className={cx('flex flex-col', className)}
    >
      <FieldFrame label={label} description={description} errorMessage={errorMessage}>
        <Group className={FIELD_SHELL}>
          <Input className={cx(INPUT, 'hx-ltr')} />
          <AriaButton
            aria-label={visible ? 'پنهان کردن رمز' : 'نمایش رمز'}
            onPress={() => setVisible((v) => !v)}
            className="grid size-9 place-items-center rounded-sm text-fg-muted hover:text-fg outline-hidden data-[focus-visible]:outline-2 data-[focus-visible]:outline-solid data-[focus-visible]:outline-focus cursor-pointer"
          >
            {visible ? (
              <EyeOff aria-hidden className="size-[18px]" />
            ) : (
              <Eye aria-hidden className="size-[18px]" />
            )}
          </AriaButton>
        </Group>
      </FieldFrame>
    </AriaTextField>
  );
}

export function SearchInput({
  label,
  placeholder,
  className,
  value,
  onChange,
}: {
  label: string;
  placeholder?: string;
  className?: string;
  value?: string;
  onChange?: (v: string) => void;
}) {
  return (
    <AriaSearchField
      aria-label={label}
      value={value}
      onChange={onChange}
      className={cx('group', className)}
    >
      <Group className={cx(FIELD_SHELL, 'h-11')}>
        <Search aria-hidden className="size-[18px] shrink-0 text-fg-subtle" />
        <Input
          placeholder={placeholder}
          className={cx(INPUT, '[&::-webkit-search-cancel-button]:hidden')}
        />
        <AriaButton
          aria-label="پاک کردن"
          className="grid size-7 place-items-center rounded-xs text-fg-subtle hover:text-fg group-data-[empty]:hidden cursor-pointer"
        >
          <X aria-hidden className="size-4" />
        </AriaButton>
      </Group>
    </AriaSearchField>
  );
}

/* ───────── AmountInput (shell only — no financial logic) ───────── */

export interface AmountInputProps extends FieldBaseProps {
  /** Canonical decimal string controlled by the caller (never a JS number). */
  value: string;
  onChange: (v: string) => void;
  unit: string;
  /** Maximum decimal places accepted for this asset (e.g. Toman = 1, USDT = 6). */
  scale: number;
  hint?: ReactNode;
  placeholder?: string;
}

/**
 * Accepts digits only (Persian/Arabic digits normalised to Latin), at most `scale` decimals.
 * It validates the *shape* of input only; balances, limits and prices are checked by the server.
 */
/** "1234567.89" → "1,234,567.89" (string-only; the fractional part is left untouched). */
export function groupDigits(v: string): string {
  const [int = '', frac] = v.split('.');
  const g = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return frac === undefined ? g : `${g}.${frac}`;
}

export function AmountInput({
  label,
  value,
  onChange,
  unit,
  scale,
  hint,
  description,
  errorMessage,
  className,
  placeholder = '0',
  ...rest
}: AmountInputProps) {
  const pattern = scale > 0 ? new RegExp(`^\\d*(\\.\\d{0,${scale}})?$`) : /^\d*$/;
  return (
    <AriaTextField
      {...rest}
      // Owner (2026-10-14): thousands separators while typing. Display-only string grouping — the
      // value handed back stays the plain decimal string (no number math, no rounding).
      value={groupDigits(value)}
      onChange={(raw) => {
        const next = normalizeDigits(raw.trim()).replace(/[,٬،]/g, '');
        if (pattern.test(next)) onChange(next);
      }}
      className={cx('flex flex-col', className)}
    >
      <div className="mb-1.5 flex items-baseline justify-between gap-3">
        <Label className="text-[13px] font-medium text-fg-muted">{label}</Label>
        {hint && <span className="text-xs text-fg-subtle">{hint}</span>}
      </div>
      <Group className={cx(FIELD_SHELL, 'h-15 rounded-lg')}>
        <Input
          inputMode="decimal"
          autoComplete="off"
          placeholder={placeholder}
          className={cx(INPUT, 'hx-num text-xl font-bold text-start pointer-coarse:text-xl')}
        />
        <span className="shrink-0 rounded-sm bg-surface-1 px-2.5 py-1 text-xs font-semibold text-fg-muted">
          {unit}
        </span>
      </Group>
      {description && (
        <Text slot="description" className="mt-1.5 text-xs text-fg-subtle">
          {description}
        </Text>
      )}
      <FieldError className="mt-1.5 text-xs text-negative">{errorMessage}</FieldError>
    </AriaTextField>
  );
}
